import {
    CoreSwitchView,
    CoreTextView,
    type TCoreCheckboxOptions,
    type TCoreCheckboxStyleProps,
    type TCoreInputData,
    type TCoreTextStyleProps,
} from '@core/widgets';
import { TWidget, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { ChangeEventHandler } from 'react';
import { TextStyle } from '../../types/styles';
import styles from './checkBoxSwitcher.module.css';

const LABEL_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: 'var(--color-grey_72_lc)',
    margin: { right: 24 },
};

const SWITCH_STYLE_PROPS: TCoreCheckboxStyleProps = {
    width: 48,
    height: 28,
    borderStroke: 0,
    borderRadius: 14,
    thumbSize: 20,
    thumbSizeChecked: 20,
    thumbOffset: 4,
    thumbOffsetChecked: 4,
    main: 'var(--color-purple_100)',
    mainContainer: 'var(--color-white)',
    outline: 'var(--color-white)',
    surfaceContainerHighest: 'var(--color-grey_40_lc)',
};

export type TSwitcherViewProps = {
    uid?: string;
    widget?: TWidget<TCoreInputData, TCoreCheckboxOptions>;
    label?: string;
    isChecked: boolean;
    handleChange: ChangeEventHandler<HTMLInputElement>;
    isDisabled?: boolean;
    isHide?: boolean;
    margin?: TWidgetCommonOption['margin'];
    style?: string;
    parentStyleNames?: string[];
};

export const SwitcherView = ({ uid, widget, label, isChecked, handleChange, isDisabled, isHide, margin, style, parentStyleNames }: TSwitcherViewProps) => {
    const switchWidget: TWidget<TCoreInputData, TCoreCheckboxOptions> | undefined = widget
        ? { ...widget, options: { ...widget.options, margin: undefined } }
        : undefined;

    const wrapperStyle: React.CSSProperties = {
        ...(isHide ? { display: 'none' } : null),
        ...(margin?.top !== undefined ? { marginTop: `${margin.top}px` } : null),
        ...(margin?.right !== undefined ? { marginRight: `${margin.right}px` } : null),
        ...(margin?.bottom !== undefined ? { marginBottom: `${margin.bottom}px` } : null),
        ...(margin?.left !== undefined ? { marginLeft: `${margin.left}px` } : null),
    };

    return (
        <div data-test={uid} style={wrapperStyle} className={clsx(styles.switchWrapper, isDisabled && styles.disabled)}>
            <div className={styles.switchInputBlock}>
                {label && <CoreTextView as="span" text={label} styleProps={LABEL_STYLE_PROPS} />}
                <CoreSwitchView
                    widget={switchWidget}
                    uid={uid}
                    isChecked={isChecked}
                    handleChange={handleChange}
                    isDisabled={isDisabled}
                    isHide={isHide}
                    styleName={style ?? 'switchDefault'}
                    parentStyleNames={parentStyleNames}
                    styleProps={SWITCH_STYLE_PROPS}
                />
            </div>
        </div>
    );
};
